import React from 'react';
import styled from 'styled-components';

import { formatCurrency } from 'shared/helpers/utils';
import type { PosOrderFieldsFragment } from 'src/gql/graphql';

type InstoreReceiptTotalsProps = {
  posOrder: PosOrderFieldsFragment;
};

export function InstoreReceiptTotals({ posOrder }: InstoreReceiptTotalsProps): JSX.Element {
  const { subTotal, totalDiscount, fees, totalTax, tipAmount, grandTotal } = posOrder;

  const nonNullFees = fees ?? [];

  return (
    <>
      <SubtotalTable>
        <tbody>
          <tr>
            <td>Subtotal:</td>
            <Spacer />
            <td>{formatCurrency(subTotal, { trimZeroCents: false })}</td>
          </tr>

          {totalDiscount > 0 && (
            <tr>
              <td>Discounts:</td>
              <Spacer />
              <td>{`-${formatCurrency(totalDiscount, { trimZeroCents: false })}`}</td>
            </tr>
          )}

          {nonNullFees.map((fee, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`${fee.name}-${index}`}>
              <td>{fee.name}:</td>
              <Spacer />
              <td>{formatCurrency(fee.amount ?? 0, { trimZeroCents: false })}</td>
            </tr>
          ))}

          {totalTax > 0 && (
            <tr>
              <td>Taxes:</td>
              <Spacer />
              <td>{formatCurrency(totalTax, { trimZeroCents: false })}</td>
            </tr>
          )}

          {tipAmount > 0 && (
            <tr>
              <td>Tip:</td>
              <Spacer />
              <td>{formatCurrency(tipAmount, { trimZeroCents: false })}</td>
            </tr>
          )}
        </tbody>
      </SubtotalTable>

      <Total data-testid='instore-receipt-grand-total-row'>
        <span>Order Total:</span>
        {formatCurrency(grandTotal, { trimZeroCents: false })}
      </Total>
    </>
  );
}

const SubtotalTable = styled.table`
  text-align: left;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[30]};
  margin-bottom: 10px;
  border-collapse: collapse;
  line-height: 135%;

  tbody > tr > td {
    vertical-align: top;
    white-space: nowrap;
    padding-bottom: 10px;

    &:first-child {
      text-transform: capitalize;
    }

    &:last-child {
      font-weight: bold;
      text-align: right;
    }
  }

  tbody > tr:last-child > td {
    padding-bottom: 0;
  }
`;

const Spacer = styled.td`
  width: 100%;
`;

const Total = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  color: ${({ theme }) => theme.colors.grey[30]};
  padding-top: 20px;
  display: flex;
  font-weight: bold;
  justify-content: space-between;
  font-size: 16px;

  span {
    text-transform: uppercase;
    white-space: nowrap;
  }
`;
